import { StatsigBootstrapProvider } from "@statsig/next";
import { ReactNode } from "react";
import { getExternalAccountId } from "@repo/sdk";
import { getStatsigTier } from "../config";
import { warnMissingExternalAccountId } from "../warn-missing-external-account-id";

/**
 * Source of truth for the cookie name lives in
 * `@repo/session/user-tokens-cookie` (`FELT_USER_TOKENS_COOKIE_NAME`).
 * Hardcoded here to avoid a `@repo/statsig` → `@repo/session` package
 * dep, which would form a cycle (`@repo/session` imports from
 * `@repo/statsig/flags` in the Kinsa edge auth path). Keep these strings
 * in sync.
 */
const FELT_USER_TOKENS_COOKIE_NAME = "FELT_USER_TOKENS";

/**
 * Server-side mirror of the client-side `useTrustedUserEmail` hook in
 * `apps/platform/src/lib/owlet-trusted-email.ts`. Decodes the Owlet bridge
 * `id_token` JWT from the `FELT_USER_TOKENS` cookie and returns the
 * `user_email` claim — the same value the create-patient form autofills.
 *
 * No signature verification — the middleware already validated the
 * token via `validateOwletSession` before any server component runs, so
 * by the time we reach this read it's been verified upstream. We treat
 * the claim as a trusted property, not as an auth credential.
 *
 * Returns undefined when:
 *   - The cookie isn't set yet (pre-auth users on cold start)
 *   - The JSON is malformed (cookie tampering / partial write)
 *   - The `idToken` field is missing (e.g. Kinsa cookie shape)
 *   - The JWT has been refresh-rotated — Firebase strips `user_email`
 *     on refresh-issued tokens, same caveat as the client-side hook
 *
 * `StatsigObserver` (client-side) attaches `account.email` from the SDK
 * initial context as a fallback when this returns undefined.
 */
function readUserEmailFromJwt(jwt: string): string | null {
  try {
    const parts = jwt.split(".");
    if (parts.length < 2) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), "=");
    const payload = JSON.parse(atob(padded)) as Record<string, unknown>;
    return typeof payload.user_email === "string" && payload.user_email
      ? payload.user_email
      : null;
  } catch {
    return null;
  }
}

async function getOwletTrustedEmail(): Promise<string | undefined> {
  const { cookies } = await import("next/headers");
  const cookieStore = await cookies();
  const raw = cookieStore.get(FELT_USER_TOKENS_COOKIE_NAME)?.value;
  if (!raw) return undefined;
  try {
    const tokens = JSON.parse(decodeURIComponent(raw)) as Record<
      string,
      unknown
    >;
    if (typeof tokens.idToken !== "string") return undefined;
    return readUserEmailFromJwt(tokens.idToken) ?? undefined;
  } catch {
    return undefined;
  }
}

/**
 * Statsig provider that wraps children with StatsigBootstrapProvider.
 * Reads the `external-account-id` cookie server-side and passes it as
 * `userID` — the sole unit of cohorting (see TAXONOMY.md) — so the SDK
 * boots with identity populated and every event fired from the first
 * render carries it. For Owlet, also decodes the
 * bridge `id_token` JWT to bind `email` at boot — the trusted email is
 * a JWT claim that's already in the auth cookie, so reading it here
 * costs nothing and gets email onto the very first event.
 *
 * `StatsigObserver` (mounted alongside this provider in the layout)
 * attaches `account.email` post-boot as a fallback for cases where the
 * server-side decode returns undefined (pre-auth cold start, refresh-
 * rotated tokens, or non-Owlet brands).
 */
export async function StatsigProvider({ children }: { children: ReactNode }) {
  if (
    !process.env.NEXT_PUBLIC_STATSIG_API_KEY ||
    !process.env.STATSIG_SERVER_SECRET_KEY
  ) {
    // In development/test environments, render children without Statsig
    console.warn(
      "[Statsig] Missing environment keys - rendering without Statsig"
    );
    return <>{children}</>;
  }

  const isOwlet = process.env.NEXT_PUBLIC_BRAND === "owlet";
  const [externalAccountId, email] = await Promise.all([
    getExternalAccountId(),
    isOwlet ? getOwletTrustedEmail() : Promise.resolve(undefined),
  ]);
  if (!externalAccountId) {
    // externalAccountId is the sole unit of cohorting (see TAXONOMY.md). Booting
    // without it means this session is anonymous for experiments. This is the
    // canonical once-per-request signal — the lower-level experiment/config
    // evals stay silent so a single render doesn't emit duplicate warnings.
    // Expected for genuinely pre-auth traffic (cold start before the client
    // cookie write); surfaced so we can distinguish anomalies from noise.
    warnMissingExternalAccountId({
      message:
        "StatsigProvider booted without externalAccountId — session is anonymous for experiments",
      action: "boot",
    });
  }
  const user = {
    ...(externalAccountId ? { userID: externalAccountId } : {}),
    ...(email ? { email } : {}),
  };
  const tier = getStatsigTier();

  return (
    <StatsigBootstrapProvider
      user={user}
      clientKey={process.env.NEXT_PUBLIC_STATSIG_API_KEY}
      serverKey={process.env.STATSIG_SERVER_SECRET_KEY}
      clientOptions={{ environment: { tier } }}
      serverOptions={{ environment: { tier } }}
    >
      {children}
    </StatsigBootstrapProvider>
  );
}

export default StatsigProvider;
